<style scoped>
</style>

<template>
  <v-card>
    <v-toolbar color="#3BA9A1" dark flat>
      <v-app-bar-nav-icon></v-app-bar-nav-icon>
      <v-toolbar-title>Deployed Branches</v-toolbar-title>
      <template v-slot:extension>
        <v-tabs v-model="tab" align-with-title>
          <v-tabs-slider color="#291843"></v-tabs-slider>
          <v-tab v-for="(item, index) in items" :key="item" :class="{active: currentTab === index}" @click="currentTab = index">
            {{ item }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>
    <v-card-title>
      <v-text-field 
        v-model="search" 
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details>
      </v-text-field>
    </v-card-title>
    <v-tabs-items v-model="tab">
      <v-tab-item v-for="item in items" :key="item">
        <v-card flat>
          <div v-show="currentTab === 0">
            <v-data-table
              :headers="headers"
              :items="deployedFrontendApps"
              :search="search"
              :loading="isLoadingFrontend"
              loading-text="Loading Frontend Apps..."
              :footer-props="{'items-per-page-options': [20, 50, 100]}"
              :items-per-page="100"
            ></v-data-table>
          </div>
          <div v-show="currentTab === 1">
            <v-data-table
              :headers="headers"
              :items="deployedBackendApps"
              :search="search"
              :loading="isLoadingBackend"
              loading-text="Loading Backend Apps..."
              :footer-props="{'items-per-page-options': [20, 50, 100]}"
              :items-per-page="100"
            ></v-data-table>
          </div>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import DeploymentService from '../DeploymentService.js';

export default {
  name: 'DeploymentComponent',
  data: function () {
    return {
      tab: null,
      currentTab: 0,
      search: '',
      envs: [],
      frontendApps: [],
      backendApps: [],
      headers: [
        {
          text: 'Environment',
          align: 'start',
          value: 'env'
        },
        {
          text: 'App',
          value: 'app'
        },
        {
          text: 'Branch',
          value: 'branch'
        },
        {
          text: 'Author',
          value: 'author'
        },
        {
          text: 'Date',
          value: 'date'
        }
      ],
      items: ['Frontend', 'Backend'],
      deployedFrontendApps: [],
      deployedBackendApps: [],
      error: '',
      isLoadingFrontend: true,
      isLoadingBackend: true
    }
  },
  async created () {
    try {
      /*
        gets the branch information from the highest version deployed for the inputted frontend app
        example inputs:
          ('hdvi-demo', 'account.gohdvi.com', 1)
          ('hdvi-qa1', 'fleet.gohdvi.com', 391)
        example output:
          {
            branch: 'release/v1.54.0',
            author: 'joemama',
            date: '8/11/2022, 2:03:40 PM'
          }
       */
      const getFrontendBranch = async info => {
        const branch = await DeploymentService.getFrontendBranch(info.env, info.app);
        return branch;
      };

      /*
        gets the branch information for the inputted backend app
        example inputs:
          ('demo', 'hdvi-account')
          ('qa1', 'hdvi-fleet')
        example output:
          {
            branch: 'release/v1.54.0',
            author: 'joemama',
            date: '8/11/2022, 2:03:40 PM'
          }
       */
      const getBackendBranch = async info => {
        const branch = await DeploymentService.getBackendBranch(info.env, info.app);
        return branch;
      };

      this.envs = ['dev1', 'dev2', 'qa1', 'uat1', 'demo', 'staging', 'production'];
      this.backendApps = ['account', 'agent', 'analytics', 'auth', 'bus', 'document', 'fleet', 'notification', 'policy', 'provision', 'public-api', 'reward'];
      this.frontendApps = ['account', 'agent', 'fleet', 'login', 'provision'];

      /*
        creates a new array frontendAppInfo, and then compiles all environment and app info into 
        an object that is pushed into the new array
        example output:
          [
            {app: 'account.gohdvi.com', env: 'hdvi-demo'},
            ...
            {app: 'fleet.gohdvi.com', env: 'hdvi-qa3'},
            ...
            {app: 'provision.gohdvi.com', env: 'hdvi-uat1'}
          ]
       */
      let frontendAppInfo = [];
      for (let i = 0, ilen = this.envs.length; i < ilen; i += 1) {
        for (let j = 0, jlen = this.frontendApps.length; j < jlen; j += 1) {
          frontendAppInfo.push({
            env: `hdvi-${this.envs[i]}`,
            app: this.frontendApps[j]
          });
        }
      }

      /*
        creates a new array backendAppInfo, and then compiles all environment and app info into 
        an object that is pushed into the new array
        example output:
          [
            {app: 'hdvi-account', env: 'demo'},
            ...
            {app: 'hdvi-fleet', env: 'qa3'},
            ...
            {app: 'hdvi-reward', env: 'uat1'}
          ]
       */
      let backendAppInfo = [];
      for (let i = 0, ilen = this.envs.length; i < ilen; i += 1) {
        for (let j = 0, jlen = this.backendApps.length; j < jlen; j += 1) {
          backendAppInfo.push({
            env: `hdvi-${this.envs[i]}`,
            app: this.backendApps[j]
          });
        }
      }

      /*
        this grabs all frontend branch information, and then pushes that information into
        the global variable deployedFrontendApps that displays on the site
       */
      const allFrontendBranches = await Promise.all(frontendAppInfo.map(getFrontendBranch));
      let branchInfo;
      for (let i = 0, ilen = frontendAppInfo.length; i < ilen; i += 1) {
        branchInfo = allFrontendBranches[i];
        this.deployedFrontendApps.push({
          env: frontendAppInfo[i].env, 
          app: frontendAppInfo[i].app, 
          branch: branchInfo.branch, 
          author: branchInfo.author, 
          date: branchInfo.date ? `${branchInfo.date} EST`: ''
        });
      }
      this.isLoadingFrontend = false;

      /*
        this grabs all backend branch information, and then pushes that information into
        the global variable deployedBackendApps that displays on the site
       */
      const allBackendBranches = await Promise.all(backendAppInfo.map(getBackendBranch));
      for (let i = 0, ilen = backendAppInfo.length; i < ilen; i += 1) {
        branchInfo = allBackendBranches[i];
        this.deployedBackendApps.push({
          env: backendAppInfo[i].env, 
          app: backendAppInfo[i].app, 
          branch: branchInfo.branch, 
          author: branchInfo.author, 
          date: branchInfo.date ? `${branchInfo.date} EST`: ''});
      }
      this.isLoadingBackend = false;
    } catch(err) {
      this.error = err;
    }
  }
};
</script>